"use client";
import { level3 } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaPlay } from "react-icons/fa6";

const Level3 = () => {
  const [isPopupOpen, setPopupOpen] = useState({ open: false, code: "" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [slidesToScroll, setSlidesToScroll] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    slidesToScroll,
  });

  useEffect(() => {
    const updateSlidesToScroll = () => {
      setSlidesToScroll(window.innerWidth >= 1024 ? 4 : 3);
      setIsMobile(window.innerWidth < 768);
    };
    updateSlidesToScroll();
    window.addEventListener("resize", updateSlidesToScroll);
    return () => window.removeEventListener("resize", updateSlidesToScroll);
  }, []);

  useEffect(() => {
    emblaApi?.reInit({ loop: false, slidesToScroll });
  }, [emblaApi, slidesToScroll]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const tryReinit = () => {
      try {
        emblaApi.reInit();
      } catch (e) {}
    };
    if (document.readyState === "complete") {
      tryReinit();
    } else {
      window.addEventListener("load", tryReinit, { once: true });
      return () => window.removeEventListener("load", tryReinit);
    }
  }, [emblaApi]);

  const openPopup = (code: string) => {
    setPopupOpen({ open: true, code: code });
  };

  const closePopup = () => {
    setPopupOpen({ open: false, code: "" });
  };
  return (
    <div className="w-full bg-[#000]">
      <div className="main_container mx-auto relative px-5 lg:py-8 py-5 flex flex-col gap-3 md:gap-6">
        <div>
          <h4 className="text-white text-base font-bold">
            <span className="text-[#FFBA08]">Level 3:</span> Firm-Wide AI
            Operating Model
          </h4>
          <p className="text-sm text-[#FFFFFF99]">
            Leading AI Transformation - Governance, Controls, Agent Evaluation &
            the Deployment Playbook
          </p>
        </div>
        <div
          className="embla overflow-hidden rounded-lg h-[300px] md:h-[430px]"
          ref={emblaRef}
        >
          <div className="embla__container flex h-full">
            {level3?.map((instructor, index) => (
              <div
                key={index}
                className="embla__slide h-full flex-[0_0_50%] lg:flex-[0_0_22%] pr-2 md:pr-5"
              >
                <div
                  className="relative cursor-pointer h-full"
                  onClick={() => {
                    if (isMobile && instructor.youtubeVideo) {
                      openPopup(instructor.youtubeVideo);
                    }
                  }}
                >
                  <SingleInstructorUI
                    personImg={instructor.imagekitUrl}
                    title={instructor.name}
                    youTubeLink={instructor.youtubeVideo}
                    openPopup={openPopup}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden md:flex items-center justify-center gap-3 md:gap-4">
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            aria-label="Previous slide"
            className="w-7 h-7 md:w-9 md:h-9 rounded-full border border-white/40 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            <FaChevronLeft className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
          </button>
          <div className="flex items-center gap-1.5">
            {scrollSnaps.map((_, index) => (
              <div
                key={index}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all ${
                  index === selectedIndex
                    ? "w-4 bg-[#2E6FF2]"
                    : "w-2 bg-white/30"
                }`}
              />
            ))}
          </div>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            aria-label="Next slide"
            className="w-7 h-7 md:w-9 md:h-9 rounded-full border border-white/40 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            <FaChevronRight className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
          </button>
        </div>
      </div>
      {isPopupOpen.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999]">
          <div className="bg-white p-1 shadow-lg relative rounded-[14px] md:w-[auto] w-[85%]">
            <button
              onClick={closePopup}
              className="mt-4 bg-red-700 text-white px-3 py-1 rounded absolute right-[-15px] top-[-30px]"
            >
              ✕
            </button>
            <iframe
              className="lg:w-[600px] lg:h-[320px] w-full h-[180px] rounded-[14px]"
              width="100%"
              height="320"
              id="instructor-video-player-field"
              src={`https://www.youtube.com/embed/${isPopupOpen.code}`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};
const SingleInstructorUI = ({
  personImg,
  title,
  youTubeLink,
  openPopup,
}: {
  personImg: string;
  title: React.ReactNode;
  youTubeLink?: string;
  openPopup?: (_code: string) => void;
}) => {
  return (
    <div className="relative overflow-hidden rounded-lg bg-[#07121a] group h-full">
      <div className="relative w-full h-full">
        <Image
          src={personImg}
          alt={typeof title === "string" ? title : "Instructor image"}
          fill
          quality={80}
          loading="lazy"
        />
      </div>

      <div className="scale-y-100 md:scale-y-0 md:group-hover:scale-y-100 origin-bottom transition-transform duration-500 absolute inset-0 ">
        <div className="w-full h-full bg-[linear-gradient(180deg,rgba(4,16,28,0)_0%,#04101C_76.6%)] p-2 md:p-6 flex items-end">
          <div className="text-white flex justify-center flex-col gap-2">
            <div className="relative">
              <p
                className={
                  "peer text-white font-bold text-xs md:text-sm leading-tight overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:1] z-40"
                }
              >
                {title}
              </p>
            </div>
            <div>
              {youTubeLink ? (
                <button
                  onClick={() => openPopup && openPopup(youTubeLink)}
                  className="rounded-full md:rounded-[80px] text-[10px] md:text-sm bg-white text-black inline-flex px-2 md:px-8 py-[2px] md:py-[6px] gap-[6px] items-center"
                >
                  <FaPlay className="w-2 md:w-5" /> Trailer
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Level3;
